import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Building2, Users, Crown, Shield, Settings, Plus, Pencil, Trash2, RefreshCw,
  Building, Globe, Lock, ChevronRight, CheckCircle2, XCircle, AlertTriangle,
  Layers, ArrowRightLeft, Database, CreditCard, UserPlus, UserMinus,
  FileText, Download, Receipt, Landmark, Search, Save, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_COLORS = {
  STANDARD_FREE: 'bg-gray-100 text-gray-700',
  TRIAL: 'bg-blue-100 text-blue-700',
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-700',
  ACTIVE_PREMIUM: 'bg-green-100 text-green-700',
  PAST_DUE: 'bg-orange-100 text-orange-700',
  SUSPENDED: 'bg-red-100 text-red-700',
  CANCELLED_ACTIVE: 'bg-amber-100 text-amber-700',
  CANCELLED_EXPIRED: 'bg-gray-200 text-gray-600',
  PAUSED: 'bg-purple-100 text-purple-700',
  PENDING_SITE_MIGRATION: 'bg-cyan-100 text-cyan-700',
  DOWNGRADED: 'bg-rose-100 text-rose-700',
  BLOCKED: 'bg-red-200 text-red-800',
};

const STATE_LABELS = {
  STANDARD_FREE: 'Standard (Frei)',
  TRIAL: 'Testphase',
  PENDING_PAYMENT: 'Zahlung ausstehend',
  ACTIVE_PREMIUM: 'Aktiv Premium',
  PAST_DUE: 'Überfällig',
  SUSPENDED: 'Gesperrt',
  CANCELLED_ACTIVE: 'Gekündigt (aktiv)',
  CANCELLED_EXPIRED: 'Gekündigt (abgelaufen)',
  PAUSED: 'Pausiert',
  PENDING_SITE_MIGRATION: 'Gruppenwechsel',
  DOWNGRADED: 'Herabgestuft',
  BLOCKED: 'Blockiert',
};

const SUBSCRIPTION_STATES = Object.keys(STATE_LABELS);

// ─── Tier Dialog ─────────────────────────────────────────────────────
const TierDialog = ({ open, onClose, onSave, editData }) => {
  const blank = {
    tier_type: 'site', name: '', label: '', price_monthly_net: 0, commission_rate: 10,
    listing_limit: '', priority_placement: false, priority_support: false,
    dedicated_account_manager: false, max_member_sites: '', features: '', active: true
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm({
        ...blank, ...editData,
        listing_limit: editData.listing_limit ?? '',
        max_member_sites: editData.max_member_sites ?? '',
        features: (editData.features || []).join(', '),
      });
    } else {
      setForm(blank);
    }
  }, [editData, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.label) return toast.error('Name und Label sind Pflicht');
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_monthly_net: parseFloat(form.price_monthly_net) || 0,
        commission_rate: parseFloat(form.commission_rate) || 10,
        listing_limit: form.listing_limit ? parseInt(form.listing_limit) : null,
        max_member_sites: form.max_member_sites ? parseInt(form.max_member_sites) : null,
        features: form.features ? form.features.split(',').map(f => f.trim()).filter(Boolean) : [],
      };
      await onSave(payload);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editData ? 'Tier bearbeiten' : 'Neues Tier erstellen'}</DialogTitle>
          <DialogDescription>Subscription-Tier konfigurieren</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={form.tier_type} onValueChange={v => set('tier_type', v)}>
            <SelectTrigger data-testid="tier-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="site">Standort-Tier</SelectItem>
              <SelectItem value="group">Gruppen-Tier</SelectItem>
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Interner Name *" value={form.name} onChange={e => set('name', e.target.value)} data-testid="tier-name-input" />
            <Input placeholder="Anzeige-Label *" value={form.label} onChange={e => set('label', e.target.value)} data-testid="tier-label-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 border rounded-md px-3">
              <span className="text-muted-foreground text-sm shrink-0">€</span>
              <Input type="number" placeholder="Preis/Monat" className="border-0 p-0 h-9" value={form.price_monthly_net} onChange={e => set('price_monthly_net', e.target.value)} data-testid="tier-price-input" />
            </div>
            <div className="flex items-center gap-2 border rounded-md px-3">
              <Input type="number" placeholder="Provision %" className="border-0 p-0 h-9" value={form.commission_rate} onChange={e => set('commission_rate', e.target.value)} data-testid="tier-rate-input" />
              <span className="text-muted-foreground text-sm">%</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="Max. Inserate (leer = unbegrenzt)" value={form.listing_limit} onChange={e => set('listing_limit', e.target.value)} />
            {form.tier_type === 'group' && (
              <Input type="number" placeholder="Max. Standorte (leer = unbegrenzt)" value={form.max_member_sites} onChange={e => set('max_member_sites', e.target.value)} />
            )}
          </div>
          <Input placeholder="Features (Komma-getrennt)" value={form.features} onChange={e => set('features', e.target.value)} />
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.priority_placement} onCheckedChange={v => set('priority_placement', v)} />
              Priorität in Suche
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.priority_support} onCheckedChange={v => set('priority_support', v)} />
              Priority Support
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.dedicated_account_manager} onCheckedChange={v => set('dedicated_account_manager', v)} />
              Ded. Account Manager
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.active} onCheckedChange={v => set('active', v)} />
              Aktiv
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-tier-btn">{saving ? '...' : 'Speichern'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── State Transition Dialog ─────────────────────────────────────────
const StateDialog = ({ open, onClose, onSave, entityName, currentState }) => {
  const [newState, setNewState] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNewState(''); setReason(''); }, [open]);

  const handleSave = async () => {
    if (!newState) return toast.error('Bitte neuen Status auswählen');
    if (!reason.trim()) return toast.error('Grund ist Pflicht');
    setSaving(true);
    try {
      await onSave({ new_state: newState, reason, trigger: 'admin' });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Status ändern: {entityName}</DialogTitle>
          <DialogDescription>Aktueller Status: <Badge className={STATE_COLORS[currentState]}>{STATE_LABELS[currentState] || currentState}</Badge></DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={newState} onValueChange={setNewState}>
            <SelectTrigger data-testid="new-state-select"><SelectValue placeholder="Neuer Status..." /></SelectTrigger>
            <SelectContent>
              {SUBSCRIPTION_STATES.filter(s => s !== currentState).map(s => (
                <SelectItem key={s} value={s}>{STATE_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder="Grund (Pflicht) *" value={reason} onChange={e => setReason(e.target.value)} data-testid="state-reason-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="confirm-state-change-btn">{saving ? '...' : 'Status ändern'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Invitation Dialog ───────────────────────────────────────────────
const InvitationDialog = ({ open, groupId, onClose, onSend }) => {
  const [type, setType] = useState('email');
  const [email, setEmail] = useState('');
  const [siteId, setSiteId] = useState('');
  const [message, setMessage] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { setEmail(''); setSiteId(''); setMessage(''); setSearchResults([]); setSearchQuery(''); }, [open]);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/invitations/search-sites?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data);
    } catch { setSearchResults([]); }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      if (type === 'email') {
        if (!email) return;
        await onSend(groupId, 'email', email, message);
      } else {
        if (!siteId) return;
        await onSend(groupId, 'site', siteId, message);
      }
      onClose();
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Einladung senden</DialogTitle>
          <DialogDescription>Standort zur Gruppe einladen</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button size="sm" variant={type === 'email' ? 'default' : 'outline'} onClick={() => setType('email')} data-testid="inv-type-email">Per E-Mail</Button>
            <Button size="sm" variant={type === 'site' ? 'default' : 'outline'} onClick={() => setType('site')} data-testid="inv-type-site">Nach Standort</Button>
          </div>
          {type === 'email' ? (
            <Input placeholder="E-Mail-Adresse des Site-Admins *" value={email} onChange={e => setEmail(e.target.value)} type="email" data-testid="inv-email-input" />
          ) : (
            <div>
              <Input placeholder="Standort suchen..." value={searchQuery} onChange={e => handleSearch(e.target.value)} data-testid="inv-site-search" />
              {searchResults.length > 0 && (
                <div className="border rounded-md mt-1 max-h-32 overflow-y-auto">
                  {searchResults.map(s => (
                    <button key={s.site_id} onClick={() => { setSiteId(s.site_id); setSearchQuery(s.site_name); setSearchResults([]); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 ${siteId === s.site_id ? 'bg-orange-50 font-medium' : ''}`}>
                      {s.site_name} — {s.city}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <Input placeholder="Persönliche Nachricht (optional, max 500 Zeichen)" value={message} onChange={e => setMessage(e.target.value)} maxLength={500} data-testid="inv-message-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSend} disabled={sending} data-testid="send-invitation-btn">{sending ? '...' : 'Einladung senden'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── SEPA Edit Dialog ─────────────────────────────────────────────────
const SepaEditDialog = ({ open, onClose, onSave, entity }) => {
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entity) {
      setIban(entity.iban || '');
      setBic(entity.bic || '');
    }
  }, [entity, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(entity.entity_type, entity.entity_id, { iban, bic });
      onClose();
    } finally { setSaving(false); }
  };

  if (!entity) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>SEPA-Daten bearbeiten</DialogTitle>
          <DialogDescription>{entity.entity_name} ({entity.entity_type === 'site' ? 'Standort' : 'Gruppe'})</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-muted-foreground">IBAN</label>
            <Input value={iban} onChange={e => setIban(e.target.value)} placeholder="DE89 3704 0044 0532 0130 00" data-testid="sepa-edit-iban" />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">BIC / SWIFT</label>
            <Input value={bic} onChange={e => setBic(e.target.value)} placeholder="COBADEFFXXX" data-testid="sepa-edit-bic" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="sepa-edit-save-btn">{saving ? '...' : 'Speichern'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────
export default function SubscriptionManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierDialog, setTierDialog] = useState({ open: false, data: null });
  const [stateDialog, setStateDialog] = useState({ open: false, entity: null, type: null });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [invStats, setInvStats] = useState({});
  const [invDialog, setInvDialog] = useState({ open: false, groupId: null });
  const [invoices, setInvoices] = useState([]);
  const [sepaMandates, setSepaMandates] = useState([]);
  const [sepaStats, setSepaStats] = useState({});
  const [sepaFilter, setSepaFilter] = useState({ type: 'all', iban: 'all', search: '' });
  const [sepaEditDialog, setSepaEditDialog] = useState({ open: false, entity: null });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, t, g, s, ph, inv, invSt, invcs, sm, ss] = await Promise.all([
        axios.get(`${API}/subscription/overview`),
        axios.get(`${API}/subscription/tiers`),
        axios.get(`${API}/groups`),
        axios.get(`${API}/sites`),
        axios.get(`${API}/payments/history`).catch(() => ({ data: [] })),
        axios.get(`${API}/invitations`).catch(() => ({ data: [] })),
        axios.get(`${API}/invitations/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/invoices`).catch(() => ({ data: [] })),
        axios.get(`${API}/subscription/sepa-mandates`).catch(() => ({ data: [] })),
        axios.get(`${API}/subscription/sepa-mandates/stats`).catch(() => ({ data: {} })),
      ]);
      setOverview(ov.data);
      setTiers(t.data);
      setGroups(g.data);
      setSites(s.data);
      setPaymentHistory(ph.data || []);
      setInvitations(inv.data || []);
      setInvStats(invSt.data || {});
      setInvoices(invcs.data || []);
      setSepaMandates(sm.data || []);
      setSepaStats(ss.data || {});
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (!user || user.role !== 'admin') { navigate('/'); return null; }

  const handleSaveTier = async (data) => {
    try {
      if (tierDialog.data) {
        await axios.put(`${API}/subscription/tiers/${tierDialog.data.tier_id}`, data);
        toast.success('Tier aktualisiert');
      } else {
        await axios.post(`${API}/subscription/tiers`, data);
        toast.success('Tier erstellt');
      }
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); throw e; }
  };

  const handleDeleteTier = async (tierId) => {
    if (!window.confirm('Tier wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/subscription/tiers/${tierId}`);
      toast.success('Tier gelöscht');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleStateChange = async (data) => {
    try {
      const { entity, type } = stateDialog;
      const id = type === 'site' ? entity.site_id : entity.group_id;
      await axios.put(`${API}/${type === 'site' ? 'sites' : 'groups'}/${id}/subscription-state`, data);
      toast.success('Status geändert');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Statusänderung nicht erlaubt'); throw e; }
  };

  const handleGrandfatheredPrice = async (entityType, entityId, price) => {
    try {
      await axios.put(`${API}/${entityType}/${entityId}/grandfathered-price`, { grandfathered_price: price || null });
      toast.success(price ? 'Bestandsschutzpreis gesetzt' : 'Bestandsschutz entfernt');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleSeedDemo = async () => {
    try {
      await axios.post(`${API}/subscription/seed-demo`);
      toast.success('Demo-Daten erstellt');
      loadAll();
    } catch (e) { toast.error('Fehler beim Erstellen der Demo-Daten'); }
  };

  const handleSeedTiers = async () => {
    try {
      await axios.post(`${API}/subscription/tiers/seed`);
      toast.success('Standard-Tiers erstellt');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleUpgradeCheckout = async (entityType, entityId, tierId) => {
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        tier_id: tierId,
        entity_type: entityType,
        entity_id: entityId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upgrade fehlgeschlagen');
    }
  };

  const handleManualPayment = async (entityType, entityId, tierId, amount) => {
    const reason = window.prompt('Grund für manuelle Zahlung:');
    if (!reason) return;
    try {
      await axios.post(`${API}/payments/manual`, {
        entity_type: entityType,
        entity_id: entityId,
        tier_id: tierId,
        amount: parseFloat(amount),
        reason,
      });
      toast.success('Manuelle Zahlung erfasst, Subscription aktiviert');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleSendInvitation = async (groupId, type, target, message) => {
    try {
      if (type === 'email') {
        await axios.post(`${API}/invitations/by-email?group_id=${groupId}`, { email: target, personal_message: message || null });
      } else {
        await axios.post(`${API}/invitations/by-site?group_id=${groupId}`, { site_id: target, personal_message: message || null });
      }
      toast.success('Einladung gesendet');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleRevokeInvitation = async (invId) => {
    if (!window.confirm('Einladung wirklich widerrufen?')) return;
    try {
      await axios.post(`${API}/invitations/${invId}/revoke`);
      toast.success('Einladung widerrufen');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleResendInvitation = async (invId) => {
    try {
      await axios.post(`${API}/invitations/${invId}/resend`);
      toast.success('Einladung erneut gesendet');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleSepaUpdate = async (entityType, entityId, data) => {
    try {
      await axios.put(`${API}/subscription/sepa-mandates/${entityType}/${entityId}`, data);
      toast.success('SEPA-Daten aktualisiert');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); throw e; }
  };

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">{[0,1,2,3].map(i => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-96 w-full" />
      </div>
    </div>
  );

  const siteTiers = tiers.filter(t => t.tier_type === 'site');
  const groupTiers = tiers.filter(t => t.tier_type === 'group');
  const stateCount = overview?.site_state_counts || {};
  const groupStateCount = overview?.group_state_counts || {};

  return (
    <div className="min-h-screen bg-background" data-testid="subscription-management-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold font-manrope flex items-center gap-2" data-testid="subscription-title">
              <CreditCard className="h-6 w-6 text-orange-500" />
              Subscription-Verwaltung
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Surplify.de · Tiers, Standorte, Gruppen & Abonnements</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSeedTiers} data-testid="seed-tiers-btn">
              <Database className="h-3.5 w-3.5 mr-1.5" />Tiers seeden
            </Button>
            <Button variant="outline" size="sm" onClick={handleSeedDemo} data-testid="seed-demo-btn">
              <Database className="h-3.5 w-3.5 mr-1.5" />Demo-Daten
            </Button>
            <Button size="sm" onClick={loadAll} variant="outline"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </div>

        {/* KPI Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Standorte</p>
                  <p className="text-2xl font-bold mt-1 font-mono">{overview?.total_sites || 0}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{stateCount.ACTIVE_PREMIUM || 0} Premium</p>
                </div>
                <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"><Building2 className="h-5 w-5" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Gruppen</p>
                  <p className="text-2xl font-bold mt-1 font-mono">{overview?.total_groups || 0}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{groupStateCount.ACTIVE_PREMIUM || 0} Aktiv</p>
                </div>
                <div className="p-2.5 rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400"><Layers className="h-5 w-5" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Bestandsschutz</p>
                  <p className="text-2xl font-bold mt-1 font-mono">{(overview?.grandfathered_sites || 0) + (overview?.grandfathered_groups || 0)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{overview?.grandfathered_sites || 0} Sites, {overview?.grandfathered_groups || 0} Gruppen</p>
                </div>
                <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400"><Lock className="h-5 w-5" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Abo-Tiers</p>
                  <p className="text-2xl font-bold mt-1 font-mono">{tiers.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{siteTiers.length} Site, {groupTiers.length} Gruppe</p>
                </div>
                <div className="p-2.5 rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400"><Crown className="h-5 w-5" /></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="tiers" className="space-y-4">
          <div className="bg-muted/50 p-1.5 rounded-lg space-y-1">
            {/* Row 1: Stammdaten */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest pl-2 pr-1 shrink-0 select-none">Stamm</span>
              <TabsList className="flex h-auto gap-1 bg-transparent p-0">
                <TabsTrigger value="tiers" data-testid="tab-tiers"><Crown className="h-3.5 w-3.5 mr-1" />Tiers</TabsTrigger>
                <TabsTrigger value="sites" data-testid="tab-sites"><Building2 className="h-3.5 w-3.5 mr-1" />Standorte <Badge className="ml-1 h-4 px-1.5 text-xs">{sites.length}</Badge></TabsTrigger>
                <TabsTrigger value="groups" data-testid="tab-groups"><Layers className="h-3.5 w-3.5 mr-1" />Gruppen <Badge className="ml-1 h-4 px-1.5 text-xs">{groups.length}</Badge></TabsTrigger>
                <TabsTrigger value="states" data-testid="tab-states"><ArrowRightLeft className="h-3.5 w-3.5 mr-1" />Status</TabsTrigger>
                <TabsTrigger value="invitations" data-testid="tab-invitations"><UserPlus className="h-3.5 w-3.5 mr-1" />Einladungen <Badge className="ml-1 h-4 px-1.5 text-xs">{invitations.length}</Badge></TabsTrigger>
              </TabsList>
            </div>
            {/* Row 2: Finanzen */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest pl-2 pr-1 shrink-0 select-none">Finanz</span>
              <TabsList className="flex h-auto gap-1 bg-transparent p-0">
                <TabsTrigger value="payments" data-testid="tab-payments"><CreditCard className="h-3.5 w-3.5 mr-1" />Zahlungen <Badge className="ml-1 h-4 px-1.5 text-xs">{paymentHistory.length}</Badge></TabsTrigger>
                <TabsTrigger value="invoices" data-testid="tab-invoices"><FileText className="h-3.5 w-3.5 mr-1" />Rechnungen <Badge className="ml-1 h-4 px-1.5 text-xs">{invoices.length}</Badge></TabsTrigger>
                <TabsTrigger value="sepa" data-testid="tab-sepa"><Landmark className="h-3.5 w-3.5 mr-1" />SEPA-Mandate <Badge className="ml-1 h-4 px-1.5 text-xs">{sepaStats.total_with_iban || 0}</Badge></TabsTrigger>
                <TabsTrigger value="exports" data-testid="tab-exports"><Download className="h-3.5 w-3.5 mr-1" />Exporte</TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* Tiers Tab */}
          <TabsContent value="tiers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Subscription Tiers</CardTitle>
                    <CardDescription>Alle Tier-Parameter sind admin-editierbar. Preisänderungen gelten nur für neue Abos.</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => setTierDialog({ open: true, data: null })} data-testid="add-tier-btn">
                    <Plus className="h-4 w-4 mr-1" />Neues Tier
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Standort-Tiers</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {siteTiers.map(t => (
                      <Card key={t.tier_id} className={`border-l-4 ${t.name === 'premium' ? 'border-l-orange-500' : 'border-l-gray-300'}`} data-testid={`tier-card-${t.name}`}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-bold text-lg">{t.label}</p>
                              <p className="text-2xl font-mono font-bold text-orange-600 mt-1">
                                {t.price_monthly_net > 0 ? `€${t.price_monthly_net}/Mo` : 'Kostenlos'}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setTierDialog({ open: true, data: t })}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteTier(t.tier_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                          <div className="mt-3 space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-muted-foreground">Provision</span><span className="font-mono font-bold">{t.commission_rate}%</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Max. Inserate</span><span className="font-mono">{t.listing_limit ?? 'Unbegrenzt'}</span></div>
                            {t.priority_placement && <Badge variant="outline" className="text-xs bg-amber-50 border-amber-200 text-amber-700">Priorität in Suche</Badge>}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-1">
                            {(t.features || []).map((f, i) => <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mt-6">Gruppen-Tiers</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {groupTiers.map(t => (
                      <Card key={t.tier_id} className={`border-l-4 ${t.name === 'group_enterprise' ? 'border-l-orange-500' : 'border-l-blue-400'}`} data-testid={`tier-card-${t.name}`}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-bold text-lg">{t.label}</p>
                              <p className="text-2xl font-mono font-bold text-orange-600 mt-1">€{t.price_monthly_net}/Mo</p>
                            </div>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setTierDialog({ open: true, data: t })}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteTier(t.tier_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                          <div className="mt-3 space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-muted-foreground">Provision</span><span className="font-mono font-bold">{t.commission_rate}%</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Max. Standorte</span><span className="font-mono">{t.max_member_sites ?? 'Unbegrenzt'}</span></div>
                            {t.priority_support && <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">Priority Support</Badge>}
                            {t.dedicated_account_manager && <Badge variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">Account Manager</Badge>}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-1">
                            {(t.features || []).map((f, i) => <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            <TierDialog open={tierDialog.open} onClose={() => setTierDialog({ open: false, data: null })} onSave={handleSaveTier} editData={tierDialog.data} />
          </TabsContent>

          {/* Sites Tab */}
          <TabsContent value="sites">
            <Card>
              <CardHeader>
                <CardTitle>Standorte</CardTitle>
                <CardDescription>Alle registrierten Standorte mit Subscription-Status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Standort</th>
                        <th className="pb-2 font-medium text-muted-foreground">Gruppe</th>
                        <th className="pb-2 font-medium text-muted-foreground">Status</th>
                        <th className="pb-2 font-medium text-muted-foreground">Bestandsschutz</th>
                        <th className="pb-2 font-medium text-muted-foreground">Stadt</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sites.map(s => {
                        const state = s.subscription_state || 'STANDARD_FREE';
                        const group = groups.find(g => g.group_id === s.group_id);
                        return (
                          <tr key={s.site_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`site-row-${s.site_id}`}>
                            <td className="py-3 font-medium">{s.site_name}</td>
                            <td className="py-3 text-xs text-muted-foreground">{group?.group_name || '–'}</td>
                            <td className="py-3">
                              <Badge className={`text-xs ${STATE_COLORS[state]}`}>{STATE_LABELS[state] || state}</Badge>
                            </td>
                            <td className="py-3">
                              {s.grandfathered_price != null ? (
                                <Badge className="text-xs bg-emerald-100 text-emerald-700">€{s.grandfathered_price} <Lock className="h-3 w-3 ml-1" /></Badge>
                              ) : '–'}
                            </td>
                            <td className="py-3 text-xs text-muted-foreground">{s.city}, {s.country}</td>
                            <td className="py-3">
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setStateDialog({ open: true, entity: s, type: 'site' })} data-testid={`change-state-site-${s.site_id}`}>
                                  <ArrowRightLeft className="h-3 w-3 mr-1" />Status
                                </Button>
                                {state === 'STANDARD_FREE' && siteTiers.find(t => t.name === 'premium') && (
                                  <Button size="sm" variant="outline" className="h-7 text-xs text-orange-600 border-orange-200" onClick={() => handleUpgradeCheckout('site', s.site_id, siteTiers.find(t => t.name === 'premium').tier_id)} data-testid={`upgrade-site-${s.site_id}`}>
                                    <Crown className="h-3 w-3 mr-1" />Upgrade
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {!sites.length && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Keine Standorte</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups">
            <Card>
              <CardHeader>
                <CardTitle>Gruppen</CardTitle>
                <CardDescription>Gruppen mit Mitglieds-Standorten und Billing</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {groups.map(g => {
                    const state = g.subscription_state || 'STANDARD_FREE';
                    const memberSites = sites.filter(s => s.member_of_group_id === g.group_id || s.group_id === g.group_id);
                    const tier = tiers.find(t => t.tier_id === g.group_tier_id);
                    return (
                      <Card key={g.group_id} className="border-l-4 border-l-orange-400" data-testid={`group-card-${g.group_id}`}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-lg">{g.group_name}</p>
                                <Badge className={`text-xs ${STATE_COLORS[state]}`}>{STATE_LABELS[state] || state}</Badge>
                                {tier && <Badge variant="outline" className="text-xs">{tier.label}</Badge>}
                              </div>
                              {g.legal_company_name && <p className="text-sm text-muted-foreground mt-0.5">{g.legal_company_name}</p>}
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => setStateDialog({ open: true, entity: g, type: 'group' })} data-testid={`change-state-group-${g.group_id}`}>
                              <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />Status
                            </Button>
                          </div>
                          {g.grandfathered_price != null && (
                            <Badge className="text-xs bg-emerald-100 text-emerald-700 mt-2">Bestandsschutz: €{g.grandfathered_price} <Lock className="h-3 w-3 ml-1" /></Badge>
                          )}
                          <div className="mt-3">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Mitglieds-Standorte ({memberSites.length})</p>
                            <div className="space-y-1">
                              {memberSites.map(s => (
                                <div key={s.site_id} className="flex items-center gap-2 py-1 px-2 rounded bg-muted/30 text-sm">
                                  <Building className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span className="flex-1">{s.site_name}</span>
                                  {s.cost_centre_code && <Badge variant="outline" className="text-xs">{s.cost_centre_code}</Badge>}
                                  <Badge className={`text-xs ${STATE_COLORS[s.subscription_state || 'STANDARD_FREE']}`}>
                                    {STATE_LABELS[s.subscription_state || 'STANDARD_FREE']}
                                  </Badge>
                                </div>
                              ))}
                              {!memberSites.length && <p className="text-xs text-muted-foreground">Keine Mitglieder</p>}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {!groups.length && <p className="text-center text-muted-foreground py-8">Keine Gruppen</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* States Overview Tab */}
          <TabsContent value="states">
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Standort-Status-Verteilung</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {SUBSCRIPTION_STATES.map(state => {
                      const count = stateCount[state] || 0;
                      if (count === 0) return null;
                      const total = overview?.total_sites || 1;
                      return (
                        <div key={state} className="flex items-center gap-3">
                          <Badge className={`text-xs w-36 justify-center ${STATE_COLORS[state]}`}>{STATE_LABELS[state]}</Badge>
                          <div className="flex-1 bg-muted rounded-full h-2 relative overflow-hidden">
                            <div className="absolute inset-y-0 left-0 bg-orange-500 rounded-full" style={{ width: `${(count / total) * 100}%` }} />
                          </div>
                          <span className="text-sm font-mono w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Gruppen-Status-Verteilung</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {SUBSCRIPTION_STATES.map(state => {
                      const count = groupStateCount[state] || 0;
                      if (count === 0) return null;
                      const total = overview?.total_groups || 1;
                      return (
                        <div key={state} className="flex items-center gap-3">
                          <Badge className={`text-xs w-36 justify-center ${STATE_COLORS[state]}`}>{STATE_LABELS[state]}</Badge>
                          <div className="flex-1 bg-muted rounded-full h-2 relative overflow-hidden">
                            <div className="absolute inset-y-0 left-0 bg-blue-500 rounded-full" style={{ width: `${(count / total) * 100}%` }} />
                          </div>
                          <span className="text-sm font-mono w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* State Machine Reference */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base">Erlaubte Status-Übergänge</CardTitle>
                <CardDescription>Referenz: Welche Status-Wechsel sind erlaubt?</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                  {Object.entries({
                    STANDARD_FREE: ['TRIAL', 'PENDING_PAYMENT', 'ACTIVE_PREMIUM', 'PENDING_SITE_MIGRATION', 'BLOCKED'],
                    TRIAL: ['ACTIVE_PREMIUM', 'STANDARD_FREE', 'PENDING_PAYMENT', 'BLOCKED'],
                    ACTIVE_PREMIUM: ['PAST_DUE', 'CANCELLED_ACTIVE', 'PAUSED', 'PENDING_SITE_MIGRATION', 'DOWNGRADED', 'BLOCKED'],
                    PAST_DUE: ['ACTIVE_PREMIUM', 'SUSPENDED', 'BLOCKED'],
                    SUSPENDED: ['ACTIVE_PREMIUM', 'STANDARD_FREE', 'BLOCKED'],
                    CANCELLED_ACTIVE: ['ACTIVE_PREMIUM', 'CANCELLED_EXPIRED', 'BLOCKED'],
                  }).map(([from, tos]) => (
                    <div key={from} className="border rounded-lg p-3">
                      <Badge className={`text-xs mb-2 ${STATE_COLORS[from]}`}>{STATE_LABELS[from]}</Badge>
                      <div className="flex flex-wrap gap-1">
                        {tos.map(to => (
                          <span key={to} className="flex items-center gap-0.5">
                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            <Badge variant="outline" className="text-xs">{STATE_LABELS[to]}</Badge>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Zahlungshistorie</CardTitle>
                <CardDescription>Alle Subscription-Zahlungen und manuelle Buchungen</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Datum</th>
                        <th className="pb-2 font-medium text-muted-foreground">Entität</th>
                        <th className="pb-2 font-medium text-muted-foreground">Tier</th>
                        <th className="pb-2 font-medium text-muted-foreground">Betrag</th>
                        <th className="pb-2 font-medium text-muted-foreground">Status</th>
                        <th className="pb-2 font-medium text-muted-foreground">Methode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map(p => (
                        <tr key={p.transaction_id || p.session_id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="py-2.5 text-xs text-muted-foreground">{(p.created_at || '').substring(0, 10)}</td>
                          <td className="py-2.5 font-medium">{p.entity_name || p.entity_id}</td>
                          <td className="py-2.5 text-xs">{p.tier_name}</td>
                          <td className="py-2.5 font-mono">€{Number(p.amount || 0).toFixed(2)}</td>
                          <td className="py-2.5">
                            <Badge className={`text-xs ${
                              p.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                              p.payment_status === 'expired' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>{p.payment_status}</Badge>
                          </td>
                          <td className="py-2.5 text-xs text-muted-foreground">{p.payment_method || 'stripe'}</td>
                        </tr>
                      ))}
                      {!paymentHistory.length && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Keine Zahlungen</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invitations">
            <div className="grid lg:grid-cols-3 gap-4 mb-4">
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold font-mono">{invStats.total_sent || 0}</p>
                  <p className="text-xs text-muted-foreground">Gesamt gesendet</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold font-mono text-green-600">{invStats.accepted || 0}</p>
                  <p className="text-xs text-muted-foreground">Angenommen</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold font-mono text-orange-600">{invStats.acceptance_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Annahmerate</p>
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Einladungs-Log</CardTitle>
                    <CardDescription>Alle Gruppen-Einladungen</CardDescription>
                  </div>
                  <Select onValueChange={gId => setInvDialog({ open: true, groupId: gId })} value="">
                    <SelectTrigger className="w-48" data-testid="send-invitation-trigger">
                      <SelectValue placeholder="Einladung senden..." />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">ID</th>
                        <th className="pb-2 font-medium text-muted-foreground">Gruppe</th>
                        <th className="pb-2 font-medium text-muted-foreground">Empfänger</th>
                        <th className="pb-2 font-medium text-muted-foreground">Status</th>
                        <th className="pb-2 font-medium text-muted-foreground">Gesendet</th>
                        <th className="pb-2 font-medium text-muted-foreground">Ablauf</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.map(inv => (
                        <tr key={inv.invitation_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`inv-row-${inv.invitation_id}`}>
                          <td className="py-2.5 font-mono text-xs">{inv.invitation_id}</td>
                          <td className="py-2.5 font-medium text-sm">{inv.group_name}</td>
                          <td className="py-2.5 text-xs text-muted-foreground">{inv.invited_site_name || inv.invited_email}</td>
                          <td className="py-2.5">
                            <Badge className={`text-xs ${
                              inv.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                              inv.status === 'ACCEPTED' ? 'bg-green-100 text-green-700' :
                              inv.status === 'DECLINED' ? 'bg-red-100 text-red-700' :
                              inv.status === 'EXPIRED' ? 'bg-gray-100 text-gray-700' :
                              inv.status === 'REVOKED' ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-50 text-gray-500'
                            }`}>{inv.status}</Badge>
                          </td>
                          <td className="py-2.5 text-xs text-muted-foreground">{(inv.sent_at || '').substring(0, 10)}</td>
                          <td className="py-2.5 text-xs text-muted-foreground">{(inv.expiry_at || '').substring(0, 10)}</td>
                          <td className="py-2.5">
                            <div className="flex gap-1">
                              {inv.status === 'PENDING' && (
                                <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600" onClick={() => handleRevokeInvitation(inv.invitation_id)}>Widerrufen</Button>
                              )}
                              {(inv.status === 'EXPIRED' || inv.status === 'DECLINED') && (
                                <Button size="sm" variant="ghost" className="h-6 text-xs text-blue-600" onClick={() => handleResendInvitation(inv.invitation_id)}>Erneut</Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!invitations.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Keine Einladungen</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            {/* Send Invitation Dialog */}
            <InvitationDialog
              open={invDialog.open}
              groupId={invDialog.groupId}
              onClose={() => setInvDialog({ open: false, groupId: null })}
              onSend={handleSendInvitation}
            />
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Rechnungen</CardTitle>
                    <CardDescription>Alle generierten Rechnungen mit PDF-Download</CardDescription>
                  </div>
                  <Button size="sm" variant="outline" onClick={async () => {
                    const siteId = sites[0]?.site_id;
                    if (!siteId) return toast.error('Kein Standort gefunden');
                    const now = new Date();
                    const start = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
                    const end = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${new Date(now.getFullYear(), now.getMonth()+1, 0).getDate()}`;
                    try {
                      await axios.post(`${API}/invoices`, { entity_type: 'site', entity_id: siteId, billing_period_start: start, billing_period_end: end, payment_status: 'OPEN' });
                      toast.success('Rechnung erstellt');
                      loadAll();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
                  }} data-testid="create-invoice-btn"><Plus className="h-3.5 w-3.5 mr-1" />Rechnung erstellen</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Nr.</th>
                        <th className="pb-2 font-medium text-muted-foreground">Datum</th>
                        <th className="pb-2 font-medium text-muted-foreground">Kunde</th>
                        <th className="pb-2 font-medium text-muted-foreground">Netto</th>
                        <th className="pb-2 font-medium text-muted-foreground">MwSt</th>
                        <th className="pb-2 font-medium text-muted-foreground">Brutto</th>
                        <th className="pb-2 font-medium text-muted-foreground">Status</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(i => (
                        <tr key={i.invoice_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`invoice-row-${i.invoice_number}`}>
                          <td className="py-2.5 font-mono text-xs font-medium">{i.invoice_number}</td>
                          <td className="py-2.5 text-xs text-muted-foreground">{i.invoice_date}</td>
                          <td className="py-2.5 text-sm">{i.entity_name}</td>
                          <td className="py-2.5 font-mono text-xs">€{Number(i.total_net || 0).toFixed(2)}</td>
                          <td className="py-2.5 font-mono text-xs">€{Number(i.vat_amount || 0).toFixed(2)}</td>
                          <td className="py-2.5 font-mono text-xs font-bold">€{Number(i.total_gross || 0).toFixed(2)}</td>
                          <td className="py-2.5">
                            <Badge className={`text-xs ${
                              i.payment_status === 'PAID' ? 'bg-green-100 text-green-700' :
                              i.payment_status === 'FAILED' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>{i.payment_status}</Badge>
                            {i.status === 'cancelled' && <Badge className="text-xs bg-red-200 text-red-800 ml-1">STORNIERT</Badge>}
                          </td>
                          <td className="py-2.5">
                            <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => window.open(`${API}/invoices/${i.invoice_id}/pdf`, '_blank')} data-testid={`download-invoice-${i.invoice_number}`}>
                              <FileText className="h-3 w-3 mr-1" />PDF
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!invoices.length && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Keine Rechnungen</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SEPA-Mandate Tab */}
          <TabsContent value="sepa">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold font-mono">{sepaStats.total_entities || 0}</p>
                  <p className="text-xs text-muted-foreground">Gesamt Entitäten</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold font-mono text-green-600">{sepaStats.total_with_iban || 0}</p>
                  <p className="text-xs text-muted-foreground">IBAN vorhanden</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold font-mono text-orange-600">{sepaStats.total_missing_iban || 0}</p>
                  <p className="text-xs text-muted-foreground">IBAN fehlt</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold font-mono text-blue-600">{sepaStats.active_with_iban || 0}</p>
                  <p className="text-xs text-muted-foreground">Aktive mit IBAN</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle>SEPA-Mandatsverwaltung</CardTitle>
                    <CardDescription>IBAN/BIC-Daten und Mandate für alle Standorte & Gruppen</CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Select value={sepaFilter.type} onValueChange={v => setSepaFilter(f => ({ ...f, type: v }))}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid="sepa-filter-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Typen</SelectItem>
                        <SelectItem value="site">Standorte</SelectItem>
                        <SelectItem value="group">Gruppen</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={sepaFilter.iban} onValueChange={v => setSepaFilter(f => ({ ...f, iban: v }))}>
                      <SelectTrigger className="w-36 h-8 text-xs" data-testid="sepa-filter-iban">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle IBAN-Status</SelectItem>
                        <SelectItem value="present">IBAN vorhanden</SelectItem>
                        <SelectItem value="missing">IBAN fehlt</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        className="h-8 text-xs pl-8 w-44"
                        placeholder="Suchen..."
                        value={sepaFilter.search}
                        onChange={e => setSepaFilter(f => ({ ...f, search: e.target.value }))}
                        data-testid="sepa-search-input"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Typ</th>
                        <th className="pb-2 font-medium text-muted-foreground">Name</th>
                        <th className="pb-2 font-medium text-muted-foreground">Firma</th>
                        <th className="pb-2 font-medium text-muted-foreground">IBAN</th>
                        <th className="pb-2 font-medium text-muted-foreground">BIC</th>
                        <th className="pb-2 font-medium text-muted-foreground">Status</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sepaMandates
                        .filter(m => sepaFilter.type === 'all' || m.entity_type === sepaFilter.type)
                        .filter(m => {
                          if (sepaFilter.iban === 'present') return !!m.iban;
                          if (sepaFilter.iban === 'missing') return !m.iban;
                          return true;
                        })
                        .filter(m => {
                          if (!sepaFilter.search) return true;
                          const q = sepaFilter.search.toLowerCase();
                          return (m.entity_name || '').toLowerCase().includes(q)
                            || (m.legal_company_name || '').toLowerCase().includes(q)
                            || (m.iban || '').toLowerCase().includes(q);
                        })
                        .map(m => (
                          <tr key={`${m.entity_type}-${m.entity_id}`} className="border-b last:border-0 hover:bg-muted/30" data-testid={`sepa-row-${m.entity_id}`}>
                            <td className="py-2.5">
                              <Badge variant="outline" className="text-xs">
                                {m.entity_type === 'site' ? <><Building2 className="h-3 w-3 mr-1" />Site</> : <><Layers className="h-3 w-3 mr-1" />Gruppe</>}
                              </Badge>
                            </td>
                            <td className="py-2.5 font-medium text-sm">{m.entity_name}</td>
                            <td className="py-2.5 text-xs text-muted-foreground">{m.legal_company_name || '–'}</td>
                            <td className="py-2.5 font-mono text-xs">
                              {m.iban ? (
                                <span className="text-green-700">{m.iban.replace(/(.{4})/g, '$1 ').trim()}</span>
                              ) : (
                                <span className="text-orange-500 italic">fehlt</span>
                              )}
                            </td>
                            <td className="py-2.5 font-mono text-xs">{m.bic || <span className="text-muted-foreground italic">–</span>}</td>
                            <td className="py-2.5">
                              <Badge className={`text-xs ${STATE_COLORS[m.subscription_state] || 'bg-gray-100 text-gray-600'}`}>
                                {STATE_LABELS[m.subscription_state] || m.subscription_state}
                              </Badge>
                            </td>
                            <td className="py-2.5">
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSepaEditDialog({ open: true, entity: m })} data-testid={`sepa-edit-${m.entity_id}`}>
                                  <Pencil className="h-3 w-3 mr-1" />Bearbeiten
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-orange-600" onClick={() => window.open(`${API}/subscription/sepa-mandates/${m.entity_type}/${m.entity_id}/pdf`, '_blank')} data-testid={`sepa-pdf-${m.entity_id}`}>
                                  <FileText className="h-3 w-3 mr-1" />PDF
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      {!sepaMandates.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Keine SEPA-Daten gefunden</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <SepaEditDialog
              open={sepaEditDialog.open}
              onClose={() => setSepaEditDialog({ open: false, entity: null })}
              onSave={handleSepaUpdate}
              entity={sepaEditDialog.entity}
            />
          </TabsContent>

          {/* Exports Tab */}
          <TabsContent value="exports">
            <Card>
              <CardHeader>
                <CardTitle>Buchhaltungs-Exporte</CardTitle>
                <CardDescription>CSV-Exporte für Buchhaltung, DATEV, MwSt-Voranmeldung</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { key: 'revenue', icon: Receipt, title: 'Umsatzreport', desc: 'Alle Rechnungen mit Zahlungsstatus', color: 'text-green-600 bg-green-50' },
                    { key: 'datev', icon: Database, title: 'DATEV-Export', desc: 'Buchungsstapel (SKR03/SKR04)', color: 'text-blue-600 bg-blue-50' },
                    { key: 'open-items', icon: AlertTriangle, title: 'Offene Posten', desc: 'Unbezahlte & fehlgeschlagene Rechnungen', color: 'text-orange-600 bg-orange-50' },
                    { key: 'vat', icon: Globe, title: 'MwSt-Report', desc: 'Für Umsatzsteuervoranmeldung', color: 'text-violet-600 bg-violet-50' },
                    { key: 'churn', icon: ArrowRightLeft, title: 'Churn & Retention', desc: 'Kündigungen, Upgrades, Churn-Rate', color: 'text-rose-600 bg-rose-50' },
                    { key: 'invitations', icon: UserPlus, title: 'Einladungs-Analyse', desc: 'Alle Gruppen-Einladungen', color: 'text-cyan-600 bg-cyan-50' },
                  ].map(exp => (
                    <Card key={exp.key} className="border cursor-pointer hover:shadow-md transition-shadow" onClick={() => window.open(`${API}/exports/${exp.key}`, '_blank')} data-testid={`export-${exp.key}-btn`}>
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${exp.color}`}><exp.icon className="h-5 w-5" /></div>
                          <div>
                            <p className="font-semibold text-sm">{exp.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{exp.desc}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-1 text-xs text-orange-600 font-medium">
                          <Download className="h-3 w-3" />CSV herunterladen
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* State Transition Dialog */}
      {stateDialog.entity && (
        <StateDialog
          open={stateDialog.open}
          onClose={() => setStateDialog({ open: false, entity: null, type: null })}
          onSave={handleStateChange}
          entityName={stateDialog.type === 'site' ? stateDialog.entity.site_name : stateDialog.entity.group_name}
          currentState={(stateDialog.entity.subscription_state || 'STANDARD_FREE')}
        />
      )}
    </div>
  );
}
